import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
} from '@/components/ui';
import type { ArtistAdminListItem } from '@/lib/api-admin/types';
import { useArtistsQuery } from '@/features/artist/hooks/useArtistsQuery';
import { useArtistQuery } from '@/features/artist/hooks/useArtistQuery';

/** 아티스트 등록 기본 vendor(partner DEFAULT_VENDOR_ID) — 자사 공용 아티스트 표시값. */
export const SHARED_ARTIST_VENDOR = 'EVERLINE';

const DEBOUNCE_MS = 250;

export interface ArtistSelectorProps {
  value: string | null;
  vendorId?: string | null;
  disabled?: boolean;
  errorMessage?: string | null | undefined;
  onChange: (artistId: string | null) => void;
  testId?: string;
}

export const ArtistSelector = ({
  value,
  vendorId,
  disabled,
  errorMessage,
  onChange,
  testId = 'product-artist-selector',
}: ArtistSelectorProps) => {
  const { t } = useTranslation('products');
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [draft, setDraft] = useState<string | null>(value);

  useEffect(() => {
    if (open) {
      setDraft(value);
      setKeyword('');
      setDebouncedKeyword('');
    }
  }, [open, value]);

  useEffect(() => {
    if (keyword === debouncedKeyword) return;
    const id = window.setTimeout(() => setDebouncedKeyword(keyword), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [keyword, debouncedKeyword]);

  const listQuery = useArtistsQuery({
    page: 1,
    size: 10,
    sort: 'stageName,asc',
    // QA-383 — 활성만 검색하면 아티스트 관리에 보이는 보관(ARCHIVED) 아티스트가 "검색 안 됨"으로 보였다(예: 도경수).
    //   보관 아티스트도 함께 찾되, 연결은 BE 가 거부하므로(펀딩 FUNDING_ARTIST_NOT_FOUND 등) 선택은 막고 이유를 보여 준다.
    status: ['ACTIVE', 'ARCHIVED'],
    // QA-383 — 판매사로 아티스트 검색을 거르지 않는다. 아티스트 등록 화면엔 소속 판매사 입력이 없어 BE 가 기본값
    // "EVERLINE" 을 넣는데, 판매사 ID 는 "VENDOR-EVERLINE-001" 같은 실제 값이라 판매사를 먼저 고르면 아무 아티스트도
    // 검색되지 않았다. 실제 판매사가 지정된 아티스트가 다른 판매사에 붙을 때만 아래 경고(저장 시 BE 거부).
    ...(debouncedKeyword.trim() ? { keyword: debouncedKeyword.trim() } : {}),
  });

  const selectedQuery = useArtistQuery(value ?? undefined);
  const selected = selectedQuery.data ?? null;

  const items = listQuery.data?.items ?? [];

  // QA-383(BE catalog #57 · settlement #8) — 소속 판매사가 없거나 "EVERLINE"(아티스트 등록 기본값)인 아티스트는
  // 공용이라 어느 판매사 상품·펀딩에도 연결된다. 실제 판매사가 지정된 아티스트가 다른 판매사에 붙을 때만 경고.
  const vendorMismatch = useMemo(() => {
    if (!selected || !vendorId) return false;
    const artistVendor = selected.vendorId;
    if (artistVendor == null || artistVendor === '' || artistVendor === SHARED_ARTIST_VENDOR) return false;
    return artistVendor !== vendorId;
  }, [selected, vendorId]);

  const archived = selected?.status === 'ARCHIVED';

  const triggerLabel = selected
    ? selected.stageName
    : t('products.fields.artistTriggerPlaceholder', '연결할 아티스트를 선택하세요');

  const handleSelect = (artist: ArtistAdminListItem) => {
    if (artist.status === 'ARCHIVED') return;
    setDraft(artist.artistId);
  };

  const handleConfirm = () => {
    onChange(draft);
    setOpen(false);
  };

  const handleClear = () => onChange(null);

  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          data-testid={`${testId}-trigger`}
          className="flex min-w-0 flex-1 items-center justify-between rounded-md border px-3 text-left text-sm disabled:cursor-not-allowed disabled:opacity-60"
          style={{
            borderColor: archived ? 'var(--status-danger-fg)' : 'var(--divider-strong)',
            background: archived ? 'var(--status-danger-bg)' : 'var(--surface-0)',
            height: 'var(--density-compact-form-h)',
          }}
        >
          <span
            className="truncate"
            style={{ color: selected ? 'var(--content-strong)' : 'var(--content-muted)' }}
          >
            {triggerLabel}
          </span>
          <span className="ml-2 shrink-0 text-xs" style={{ color: 'var(--accent-default)' }}>
            {t('products.actions.artistSelect', '아티스트 선택')}
          </span>
        </button>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={handleClear}
            data-testid={`${testId}-clear`}
            aria-label={t('products.actions.artistClear')}
          >
            {t('products.actions.artistClearShort', '해제')}
          </Button>
        ) : null}
      </div>

      {archived ? (
        <p
          role="alert"
          aria-live="polite"
          className="rounded-md p-2 text-xs"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          {t('products.errors.artistArchived')}
        </p>
      ) : null}

      {vendorMismatch ? (
        <p
          role="alert"
          aria-live="polite"
          className="rounded-md p-2 text-xs"
          style={{
            background: 'var(--status-warn-bg)',
            color: 'var(--status-warn-fg)',
          }}
        >
          {t('products.errors.artistVendorMismatch', {
            artistVendor: selected?.vendorId ?? '—',
            productVendor: vendorId ?? '—',
          })}
        </p>
      ) : null}

      {errorMessage ? (
        <span className="text-xs" style={{ color: 'var(--signal-danger)' }}>
          {errorMessage}
        </span>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>{t('products.fields.artist', '연결 아티스트')}</DialogTitle>
            <DialogDescription>
              {t('products.fields.artistDialogDescription', '활동명 또는 별칭으로 검색해 하나를 선택하세요.')}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex flex-col gap-3">
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--content-muted)' }}
              />
              <Input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={t('products.fields.artistSearchPlaceholder')}
                className="pl-8"
                aria-label={t('products.fields.artistSearchPlaceholder')}
                data-testid={`${testId}-search`}
              />
            </div>

            <div
              className="rounded-md border"
              style={{ borderColor: 'var(--divider-strong)', background: 'var(--surface-0)' }}
            >
              <ul
                role="listbox"
                aria-label={t('products.fields.artistResults')}
                className="max-h-[360px] overflow-y-auto p-1"
              >
                {listQuery.isPending ? (
                  <li className="px-3 py-6 text-center text-xs" style={{ color: 'var(--content-muted)' }}>
                    {t('products.fields.artistLoading')}
                  </li>
                ) : listQuery.isError ? (
                  <li className="px-3 py-6 text-center text-xs" style={{ color: 'var(--signal-danger)' }}>
                    {t('products.fields.artistError')}
                  </li>
                ) : items.length === 0 ? (
                  <li className="px-3 py-6 text-center text-xs" style={{ color: 'var(--content-muted)' }}>
                    {t('products.fields.artistEmpty')}
                  </li>
                ) : (
                  items.map((a) => {
                    const isSelected = a.artistId === draft;
                    const isArchived = a.status === 'ARCHIVED';
                    return (
                      <li key={a.artistId}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          aria-disabled={isArchived}
                          disabled={isArchived}
                          onClick={() => handleSelect(a)}
                          data-testid={`${testId}-option`}
                          className={cn(
                            'flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left text-sm transition-colors',
                            'hover:bg-[color:var(--surface-1)] focus-visible:outline-none focus-visible:bg-[color:var(--surface-1)]',
                            'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent',
                            isSelected && 'bg-[color:var(--surface-1)]',
                          )}
                        >
                          <span className="flex min-w-0 flex-col">
                            <span
                              className="truncate font-medium"
                              style={{ color: isSelected ? 'var(--accent-default)' : 'var(--content-strong)' }}
                            >
                              {a.stageName}
                            </span>
                            <span className="truncate text-xs" style={{ color: 'var(--content-muted)' }}>
                              {[a.type, a.primaryAliasEn ?? undefined].filter(Boolean).join(' · ')}
                            </span>
                          </span>
                          {isArchived ? (
                            <span
                              className="shrink-0 text-[11px]"
                              style={{ color: 'var(--status-danger-fg)' }}
                              data-testid={`${testId}-archived`}
                            >
                              {t('products.fields.artistArchivedOption', '보관됨 · 아티스트 관리에서 활성화 후 연결')}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>

            <div
              className="flex justify-end gap-2 border-t pt-3"
              style={{ borderColor: 'var(--divider-strong)' }}
            >
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                {t('products.actions.cancel', '취소')}
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!draft}
                onClick={handleConfirm}
                data-testid={`${testId}-confirm`}
              >
                {t('products.actions.confirm', '확인')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
